import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(min-width: 750px)').matches) {
      this.#setupClickMenu();
      this.#setupOutsideClose(); // ★ 追加：外側クリックで閉じる
    }

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  #setupClickMenu() {
    const items = this.querySelectorAll('.menu-list__list-item');

    items.forEach((item) => {
      item.removeAttribute('on:pointerenter');
      item.removeAttribute('on:pointerleave');
      item.removeAttribute('on:focus');
      item.removeAttribute('on:blur');

      const trigger = item.querySelector('.menu-list__link[aria-haspopup="true"]');
      if (!trigger) return;

      trigger.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          this.#closeAll();
          if (!isOpen) this.#openItem(item, trigger);
        },
        { signal: this.#abortController.signal }
      );
    });
  }

  // -----------------------------------------------------
  // ★ 外側クリックでサブメニューを閉じる
  // -----------------------------------------------------
  #setupOutsideClose() {
    document.addEventListener(
      'click',
      (event) => {
        if (this.contains(event.target)) return;
        this.#closeAll();
      },
      { signal: this.#abortController.signal }
    );
  }

  // -----------------------------------------------------
  // ★ サブメニュー・オーバーフローを完全に閉じる処理
  // -----------------------------------------------------
  #closeAll() {
    const expanded = this.querySelectorAll('.menu-list__link[aria-expanded="true"]');
    expanded.forEach((t) => t.setAttribute('aria-expanded', 'false'));

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
  }

  #openItem(item, trigger) {
    this.dispatchEvent(new MegaMenuHoverEvent());
    trigger.setAttribute('aria-expanded', 'true');
    this.#state.activeItem = item;

    const submenu = item.querySelector('[ref="submenu[]"]');
    if (!submenu) return;

    const submenuHeight = submenu.offsetHeight;
    const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    const maxHeight = Math.max(submenuHeight, overflowMenuHeight);

    this.style.setProperty('--submenu-height', `${maxHeight}px`);
    this.dataset.overflowExpanded = 'true';
  }

  activate = () => {};
  deactivate = () => {};
  #activateHandler = () => {};
  #deactivate = () => {};
  #debouncedActivateHandler = debounce(() => {}, ACTIVATE_DELAY);
  #debouncedDeactivate = debounce(() => {}, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
